// =============================================================================
// TrackerFab.jsx
//
// Botão de ação flutuante (FAB) que substitui os antigos 4 botões do topo
// (New Activity / Bulk Import / Settings / Export All).
//
//   - Fixo no ecrã (acompanha o scroll); por defeito no canto inferior direito.
//   - Arrastável (Pointer Events + pointer capture, funciona com rato e toque).
//     A posição fica memorizada em localStorage e reaplicada nas visitas
//     seguintes. Fica sempre totalmente dentro do ecrã.
//   - Clique (sem arrastar) abre um menu com as ações. O menu é um popover
//     ancorado ao FAB (posição absoluta), por isso abrir NÃO desloca o botão.
//   - O menu abre para cima quando o FAB está na metade de baixo do ecrã, e
//     para baixo quando está na de cima — nunca sai da janela.
//   - Distingue arrastar de clicar por um limiar de movimento.
//
// Props:
//   actions → [{ key, label, icon, onClick, disabled?, primary? }] (já filtradas)
//   hidden  → boolean — esconde o FAB (ex.: enquanto um diálogo está aberto)
// =============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { Box, Fab, Typography } from '@mui/material';
import AddIcon   from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

const STORAGE_KEY    = 'trackerFabPosition';
const FAB_SIZE       = 56;   // diâmetro do FAB principal (px)
const MARGIN         = 24;   // margem ao canto por defeito (px)
const EDGE           = 8;    // folga mínima às margens do ecrã (px)
const DRAG_THRESHOLD = 5;    // px — abaixo disto é clique, não arrasto

// A posição é guardada como distância aos cantos (right/bottom), não como
// left/top absolutos. Assim, ao usar CSS position:fixed com right/bottom, o
// browser mantém o FAB ancorado ao canto automaticamente quando a janela é
// redimensionada/maximizada — não fica "para trás".
const defaultPos = () => ({ right: MARGIN, bottom: MARGIN });

// Mantém o FAB totalmente dentro da janela (limita os offsets right/bottom).
const clampPos = (p) => ({
  right:  Math.min(Math.max(EDGE, p.right),  Math.max(EDGE, window.innerWidth  - FAB_SIZE - EDGE)),
  bottom: Math.min(Math.max(EDGE, p.bottom), Math.max(EDGE, window.innerHeight - FAB_SIZE - EDGE)),
});

const loadPos = () => {
  try {
    const p = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (typeof p?.right === 'number' && typeof p?.bottom === 'number') return clampPos(p);
  } catch { /* ignore */ }
  return defaultPos();
};

const savePos = (p) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* ignore */ }
};

const TrackerFab = ({ actions = [], hidden = false }) => {
  const [pos,  setPos]  = useState(loadPos);
  const [open, setOpen] = useState(false);

  const dragging = useRef(false);
  const moved    = useRef(false);
  const origin   = useRef({ x: 0, y: 0, pr: 0, pb: 0 });

  // Ao redimensionar a janela, o CSS right/bottom já mantém o FAB no canto;
  // aqui só reafirmamos o clamp para o caso de a janela ficar mais pequena
  // que os offsets guardados.
  useEffect(() => {
    const onResize = () => setPos((p) => clampPos(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;   // só botão principal
    dragging.current = true;
    moved.current    = false;
    origin.current   = { x: e.clientX, y: e.clientY, pr: pos.right, pb: pos.bottom };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - origin.current.x;
    const dy = e.clientY - origin.current.y;
    if (!moved.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    moved.current = true;
    if (open) setOpen(false);                         // fecha o menu ao arrastar
    // right/bottom crescem no sentido oposto ao movimento do cursor.
    setPos(clampPos({ right: origin.current.pr - dx, bottom: origin.current.pb - dy }));
  };

  const onPointerUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (moved.current) setPos((p) => { savePos(p); return p; });
  };

  const handleClick = () => {
    if (moved.current) { moved.current = false; return; }   // foi arrasto → ignora
    setOpen((o) => !o);
  };

  const runAction = (a) => {
    if (a.disabled) return;
    setOpen(false);
    a.onClick?.();
  };

  if (!actions.length) return null;

  // Abre para cima se o FAB está na metade inferior do ecrã (offset bottom
  // pequeno = perto do fundo).
  const openUp = pos.bottom + FAB_SIZE / 2 < window.innerHeight / 2;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: pos.right, bottom: pos.bottom,
        width: FAB_SIZE, height: FAB_SIZE,
        zIndex: 1301,                 // acima do fullscreen da tabela (Dialog=1300); escondido via prop `hidden` durante os modais de edição
        display: hidden ? 'none' : 'block',
      }}
    >
      {/* Menu de ações — popover absoluto ancorado ao FAB (não desloca o botão) */}
      {open && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            ...(openUp ? { bottom: FAB_SIZE + 12 } : { top: FAB_SIZE + 12 }),
            display: 'flex',
            flexDirection: openUp ? 'column-reverse' : 'column',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          {actions.map((a) => (
            <Box key={a.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
              <Typography
                variant="caption"
                sx={{
                  bgcolor: 'background.paper',
                  border: '0.5px solid', borderColor: 'divider',
                  borderRadius: 1, px: 1, py: 0.25, boxShadow: 1,
                  color: a.disabled ? 'text.disabled' : 'text.secondary',
                }}
              >
                {a.label}
              </Typography>
              <Fab
                size="small"
                color={a.primary ? 'primary' : 'default'}
                disabled={a.disabled}
                onClick={() => runAction(a)}
                aria-label={a.label}
                sx={{ boxShadow: 2 }}
              >
                {a.icon}
              </Fab>
            </Box>
          ))}
        </Box>
      )}

      {/* FAB principal */}
      <Fab
        color="primary"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={handleClick}
        aria-label="Tracker actions"
        sx={{ cursor: 'grab', touchAction: 'none', '&:active': { cursor: 'grabbing' } }}
      >
        {open ? <CloseIcon /> : <AddIcon />}
      </Fab>
    </Box>
  );
};

export default TrackerFab;
